<script setup lang="ts">
import { useDisplay } from 'vuetify';
import type { VDialog } from 'vuetify/components';
import { SquaredIconButton } from '../squared-icon-button';
import { PrimaryActionButton } from '../primary-action-button';
import type { Promisable } from 'type-fest';
import { useLoad } from 'utils';
import { computed, ref, watch } from 'vue';
import { vElementSize } from '@vueuse/components';
import { useConfirmDialog, whenever } from '@vueuse/core';
import { noop, omit } from 'lodash';

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
type ConfirmHandler = () => Promisable<boolean | undefined | void>;
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
type CancelHandler = () => Promisable<boolean | undefined | void>;

export type DialogProps = {
  title?: string;
  subtitle?: string;
  vDialogProps?: Omit<VDialog['$props'], 'modelValue'>;
  confirmHandler?: ConfirmHandler;
  confirmDisabled?: boolean;
  confirmText?: string;
  cancelText?: string;
  cancelHandler?: CancelHandler;
  hideCancel?: boolean;
  hideExit?: boolean;
  hideActions?: boolean;
};

// TODO: emit exit when closing dialog with `esc`
const emit = defineEmits<{ exit: []; afterEnter: []; afterLeave: [] }>();

const p = withDefaults(defineProps<DialogProps>(), { confirmText: 'Conferma', cancelText: 'Annulla' });
const model = defineModel<boolean>({ default: false });

const { isRevealed, reveal, confirm, cancel } = useConfirmDialog();
const display = useDisplay();
const containerSize = ref({ width: 0, height: 0 });

const fullscreen = computed(() => {
  const containerArea = containerSize.value.width * containerSize.value.height;
  const displayArea = display.width.value * display.height.value;
  return display.width.value < 992 && (containerArea / displayArea) >= 0.5; // container is occupying 50% of the screen or more
});

const { load: onConfirm, loading: confirming } = useLoad(async (confirmHandler?: ConfirmHandler) => {
  const shouldClose = await (confirmHandler ?? p.confirmHandler)?.();
  if (shouldClose === false) return;
  confirm();
});

const { load: onCancel, loading: canceling } = useLoad(async (cancelHandler?: ConfirmHandler) => {
  const shouldClose = await (cancelHandler ?? p.cancelHandler)?.();
  if (shouldClose === false) return;
  cancel();
});

function exit() {
  model.value = false;
  emit('exit');
}

async function open() {
  const { isCanceled } = await reveal();
  return { isCanceled };
}

const confirmActionBindings = computed(() => ({
  color: 'success',
  text: p.confirmText,
  disabled: p.confirmDisabled,
  loading: confirming.value || canceling.value,
  onClick: () => onConfirm(),
  onConfirm,
  onCancel,
}));

whenever(model, () => isRevealed.value ? noop() : reveal());
whenever(() => !model.value, cancel);
watch(isRevealed, (isRevealed) => model.value = isRevealed);

defineExpose({ open, onConfirm });
</script>

<template>
  <VDialog
    v-model="model"
    :fullscreen
    :persistent="hideExit || confirming"
    width="min(1280px, 90dvw)"
    max-height="min(720px, 90dvh)"
    v-bind="vDialogProps"
    content-class="tw:transition-all"
    @after-enter="emit('afterEnter')"
    @after-leave="emit('afterLeave')"
  >
    <VCard
      v-element-size="(size) => containerSize = size"
      class="tw:space-y-1 tw:grid tw:grid-rows-[min-content_minmax(0,1fr)_min-content] tw:gap-4 tw:p-6 tw:*:p-0 tw:pb-2"
    >
      <header class="tw:grid tw:*:p-0 tw:pt-6 tw:pb-4 tw:px-8" :class="{ 'tw:grid-cols-[1fr_auto]': !hideExit, 'tw:grid-rows-2': subtitle }">
        <VCardTitle>
          <slot name="title">
            {{ title }}
          </slot>
        </VCardTitle>

        <VCardSubtitle v-if="subtitle || $slots.subtitle" class="tw:text-wrap">
          <slot name="subtitle">
            {{ subtitle }}
          </slot>
        </VCardSubtitle>

        <SquaredIconButton
          v-if="!hideExit"
          :disabled="confirming"
          class="tw:col-start-2 tw:row-start-1"
          icon="fa:fas fa-times"
          tooltip="Chiudi"
          @click="exit()"
        />

        <VDivider v-if="subtitle" class="tw:col-span-full" />
      </header>

      <VCardText class="tw:overflow-y-auto">
        <slot />
      </VCardText>

      <slot v-if="!hideActions" name="actions">
        <VCardActions class="tw:flex tw:justify-between">
          <slot v-if="!hideCancel" name="actions.cancel" :confirming :cancel>
            <PrimaryActionButton
              :text="cancelText"
              :disabled="confirming"
              variant="text"
              elevation="0"
              color="grey-darken-1"
              @click="confirmActionBindings.onCancel()"
            />
          </slot>

          <VSpacer />

          <slot name="actions.secondary" />

          <slot name="actions.confirm" v-bind="confirmActionBindings">
            <PrimaryActionButton
              v-bind="omit(confirmActionBindings, ['onConfirm', 'onClick'])"
              @click="confirmActionBindings.onConfirm()"
            />
          </slot>
        </VCardActions>
      </slot>
    </VCard>
  </VDialog>
</template>
